import axios from "axios";

const baseAPI = process.env.REACT_APP_JUSGPT_API;

export const uploadFile = async (files, accessToken) => {
  const formData = new FormData();

  // Se for um único arquivo, converte para array
  const fileArray = Array.isArray(files) ? files : [files];

  // Adiciona cada arquivo individualmente ao FormData
  fileArray.forEach(file => {
    formData.append('files', file);
  });

  const response = await axios.post(`${baseAPI}/api/upload-file`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'Authorization': accessToken
    }
  });

  return response.data;
};

export const getChats = async (accessToken, { limit = 10, offset = 0, ...rest } = {}) => {
  const response = await axios.get(`${baseAPI}/api/chats`, {
    headers: {
      'Authorization': accessToken
    },
    params: { limit, offset, ...rest }
  });

  return response.data;
}

export const getChatMessages = async (accessToken, chatId, { limit = 50, offset = 0 } = {}) => {
  const response = await axios.get(`${baseAPI}/api/chats/${chatId}/messages`, {
    headers: {
      'Authorization': accessToken
    },
    params: { limit, offset }
  });

  return response.data;
}

export const downloadTermsOfUse = async (accessToken) => {
  const response = await axios.get(`${baseAPI}/api/b2b/terms-of-use/download`, {
    headers: {
      'Authorization': accessToken
    },
    responseType: 'blob'
  });

  return { blob: response.data, filename: 'termos-de-uso.pdf' };
};

export const downloadChatFile = async (accessToken, chatId, fileId, fallbackName = 'arquivo', { inline = false } = {}) => {
  const response = await axios.get(`${baseAPI}/api/chats/${chatId}/files/${fileId}/download`, {
    headers: {
      'Authorization': accessToken
    },
    params: inline ? { inline: 1 } : undefined,
    responseType: 'blob'
  });

  const disposition = response.headers['content-disposition'] || '';
  const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;

  return { blob: response.data, filename };
}

export const setChatFileFeedback = async (accessToken, chatId, fileId, feedback) => {
  const response = await axios.patch(
    `${baseAPI}/api/chats/${chatId}/files/${fileId}/feedback`,
    { feedback },
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': accessToken
      }
    }
  );

  return response.data;
}

// JUS2-122 — voto na resposta servida pelo ai-platform-service, via proxy do
// juschat-backend. `messageId` é sempre o `upstream_message_id` da mensagem.

// Leitura em lote, uma vez por carga do chat: `?messageId=a&messageId=b`.
export const getMessageFeedbacks = async (accessToken, messageIds) => {
  const params = new URLSearchParams();
  messageIds.forEach((id) => params.append('messageId', id));

  const response = await axios.get(`${baseAPI}/api/chats/feedback`, {
    headers: {
      'Authorization': accessToken
    },
    params
  });

  return response.data;
};

// Motivos do dislike daquele turno. Só quando o popover abre.
export const getMessageFeedbackOptions = async (accessToken, messageId) => {
  const response = await axios.get(`${baseAPI}/api/chats/feedback/options`, {
    headers: {
      'Authorization': accessToken
    },
    params: { messageId }
  });

  return response.data;
};

export const saveMessageFeedback = async (accessToken, messageId, payload) => {
  const response = await axios.post(
    `${baseAPI}/api/chats/messages/${encodeURIComponent(messageId)}/feedback`,
    payload,
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': accessToken
      }
    }
  );

  return response.data;
};

export const deleteMessageFeedback = async (accessToken, messageId) => {
  await axios.delete(
    `${baseAPI}/api/chats/messages/${encodeURIComponent(messageId)}/feedback`,
    {
      headers: {
        'Authorization': accessToken
      }
    }
  );
};

export const updateChatTitle = async (accessToken, chatId, title) => {
  const response = await axios.patch(
    `${baseAPI}/api/chats/${chatId}/title`,
    { title },
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': accessToken
      }
    }
  );
  return response.data;
};

export const searchChats = async (accessToken, query) => {
  const response = await axios.get(`${baseAPI}/api/chats/search`, {
    headers: {
      'Authorization': accessToken
    },
    params: { q: query }
  });

  return response.data;
}

export const getJusGPTUsage = async (accessToken) => {
  const response = await axios.get(`${baseAPI}/api/jusgpt/usage`, {
    headers: {
      'Authorization': accessToken
    }
  });

  return response.data;
}

export const invalidateJusGPTUsage = async (accessToken) => {
  const response = await axios.post(`${baseAPI}/api/jusgpt/usage/invalidate`, {}, {
    headers: {
      'Authorization': accessToken
    }
  });

  return response.data;
}

export const getTags = async (accessToken) => {
  const response = await axios.get(`${baseAPI}/api/tags`, {
    headers: {
      'Authorization': accessToken
    }
  });

  return response.data;
}

export const getUserBusinessConfig = async (token, companyId = undefined) => {
  const response = await axios.get(`${baseAPI}/api/b2b/companies/details/${companyId ?? ''}`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return response.data;
};

export const createDraftCompany = async (token) => {
  const response = await axios.post(`${baseAPI}/api/b2b/companies/checkout/draft`, {}, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return response.data;
};

export const checkoutCompany = async ({ cardId, billingCycle }, token) => {
  const response = await axios.post(
    `${baseAPI}/api/b2b/companies/checkout`,
    { card_id: cardId, billing_cycle: billingCycle },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    }
  );
  return response.data;
};

// { valid, status, expired, nextChargeDate, checkout } do último checkout da
// IA Personalizada no addon-manager. 404 quando o usuário nunca contratou.
export const getB2BCheckoutStatus = async (token) => {
  const response = await axios.get(`${baseAPI}/api/b2b/companies/checkout-status`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return response.data;
};

export const getBusinessBannerVisibility = async (token) => {
  const response = await axios.get(`${baseAPI}/api/b2b/banner/visibility`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return response.data;
};

export const getCompanyTemplate = async (companyId, accessToken) => {
  const response = await axios.get(`${baseAPI}/api/b2b/templates/company/${companyId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
  return response.data;
};

export const createCompany = async (formData, accessToken) => {
  const response = await axios.post(`${baseAPI}/api/b2b/companies`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${accessToken}`
    }
  });
  return response.data;
};

export const updateCompany = async (companyId, formData, accessToken) => {
  const response = await axios.put(`${baseAPI}/api/b2b/companies/${companyId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${accessToken}`
    }
  });
  return response.data;
};

export const updateCompanyLogo = async (formData, accessToken) => {
  const response = await axios.put(`${baseAPI}/api/b2b/companies/logo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${accessToken}`
    }
  });
  return response.data;
};

export const updateCompanyTemplate = async (companyId, formData, accessToken) => {
  const response = await axios.put(`${baseAPI}/api/b2b/templates/company/${companyId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${accessToken}`
    }
  });
  return response.data;
};

export const createCompanyTemplate = async (formData, accessToken) => {
  const response = await axios.post(`${baseAPI}/api/b2b/templates`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${accessToken}`
    }
  });
  return response.data;
};

export const previewTemplate = async (accessToken, templateFile) => {
  const formData = new FormData();
  formData.append('template_path', templateFile);

  const response = await axios.post(`${baseAPI}/api/b2b/templates/preview`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${accessToken}`
    }
  });

  return response.data;
};

export const registerBusinessInterest = async (authToken) => {
  const response = await axios.post(`${baseAPI}/api/b2b/interest`, {}, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  return response.data;
};

export const getBusinessInterest = async (authToken) => {
  const response = await axios.get(`${baseAPI}/api/b2b/interest`, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  return response.data;
};

export const getB2BTools = async (token) => {
  const response = await axios.get(`${baseAPI}/api/b2b/tools`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return response.data;
};

export const getB2BReports = async (token) => {
  const response = await axios.get(`${baseAPI}/api/b2b/companies/stats`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return response.data;
};

export const getB2BBillingDocuments = async (token, { from, to, groupBy = 'month' } = {}) => {
  const response = await axios.get(`${baseAPI}/api/b2b/billing/documents`, {
    headers: {
      Authorization: `Bearer ${token}`
    },
    params: { from, to, groupBy, includeItems: false }
  });
  return response.data;
};

export const getCompanyUserTerms = async (authToken) => {
  const response = await axios.get(`${baseAPI}/api/b2b/company-users-terms`, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  return response.data;
};

export const acceptCompanyUserTerms = async (authToken) => {
  const response = await axios.post(
    `${baseAPI}/api/b2b/company-users-terms`,
    { accepted: true },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`
      }
    }
  );
  return response.data;
};

const driveHeaders = (token) => ({
  headers: { Authorization: `Bearer ${token}` }
});

export const getDriveFolder = async (token) => {
  const response = await axios.get(`${baseAPI}/api/drive/folders`, driveHeaders(token));
  return response.data;
};

export const createDriveFolder = async (token) => {
  const response = await axios.post(`${baseAPI}/api/drive/folders`, {}, driveHeaders(token));
  return response.data;
};

export const getDriveFilesCount = async (token) => {
  const response = await axios.get(`${baseAPI}/api/drive/folders/files-count`, driveHeaders(token));
  return response.data;
};

export const markDriveDocsReady = async (token) => {
  const response = await axios.post(`${baseAPI}/api/drive/folders/ready`, {}, driveHeaders(token));
  return response.data;
};


